import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { profitSharingService, userService } from '../services/api';
import { 
  DollarSign, Plus, Edit, Trash2, Save, X, Calendar, User, 
  CheckCircle, XCircle, TrendingUp, AlertCircle, FileText, Check 
} from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const ProfitSharingManagement = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPLR, setEditingPLR] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    user_id: '',
    period: '',
    ref_year: new Date().getFullYear(),
    amount: '',
    notes: ''
  });
  const [confirmPayslip, setConfirmPayslip] = useState({ show: false, period: null, year: null });
  const [confirmApprove, setConfirmApprove] = useState({ show: false, plrId: null });
  const [confirmMarkPaid, setConfirmMarkPaid] = useState({ show: false, plrId: null });
  const [confirmDelete, setConfirmDelete] = useState({ show: false, plrId: null });

  // Buscar funcionários (apenas GERENCIA)
  const { data: usersData } = useQuery({
    queryKey: ['users-gerencia-plr'],
    queryFn: () => userService.getUsers({ role: 'GERENCIA', limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    return [];
  }, [usersData]);

  // Buscar PLR
  const { data: plrData, isLoading } = useQuery({
    queryKey: ['profit-sharing', selectedUserId, selectedPeriod, selectedYear],
    queryFn: () => profitSharingService.getProfitSharing({
      userId: selectedUserId || undefined,
      period: selectedPeriod || undefined,
      year: selectedYear || undefined,
    }),
    staleTime: 0,
  });

  const plrList = useMemo(() => {
    if (!plrData?.data?.data) return [];
    return Array.isArray(plrData.data.data) ? plrData.data.data : [];
  }, [plrData]);

  // Mutations
  const upsertMutation = useMutation({
    mutationFn: (data) => profitSharingService.upsertProfitSharing(data),
    onSuccess: () => {
      toast.success(editingPLR ? 'PLR atualizada com sucesso!' : 'PLR criada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['profit-sharing'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar PLR');
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id) => profitSharingService.approveProfitSharing(id),
    onSuccess: () => {
      toast.success('PLR aprovada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['profit-sharing'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao aprovar PLR');
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: (id) => profitSharingService.markProfitSharingPaid(id),
    onSuccess: () => {
      toast.success('PLR marcada como paga com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['profit-sharing'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao marcar PLR como paga');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => profitSharingService.deleteProfitSharing(id),
    onSuccess: () => {
      toast.success('PLR removida com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['profit-sharing'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao remover PLR');
    },
  });

  const generatePayslipsMutation = useMutation({
    mutationFn: (data) => profitSharingService.generatePLRPayslips(data),
    onSuccess: (response) => {
      toast.success(response.data?.message || 'Holerites PLR gerados com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['profit-sharing'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao gerar holerites PLR');
    },
  });

  const resetForm = () => {
    setFormData({
      user_id: '',
      period: '',
      ref_year: new Date().getFullYear(),
      amount: '',
      notes: ''
    });
    setEditingPLR(null);
  };

  const handleOpenModal = (plr = null) => {
    if (plr) {
      setEditingPLR(plr);
      setFormData({
        user_id: plr.user_id.toString(),
        period: plr.period.toString(),
        ref_year: plr.ref_year,
        amount: plr.amount.toString(),
        notes: plr.notes || ''
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      user_id: parseInt(formData.user_id),
      period: parseInt(formData.period),
      ref_year: parseInt(formData.ref_year),
      amount: parseFloat(formData.amount),
    };
    upsertMutation.mutate(data);
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getPeriodLabel = (period) => {
    const labels = {
      4: '1º Trimestre (Abril)',
      8: '2º Trimestre (Agosto)',
      12: '3º Trimestre (Dezembro)',
    };
    return labels[period] || `Período ${period}`;
  };

  // Efeito para destacar item via URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightId = params.get('highlight');
    if (highlightId && !isLoading && plrList.length > 0) {
      const element = document.getElementById(`plr-${highlightId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('bg-blue-50');
        setTimeout(() => element.classList.remove('bg-blue-50'), 3000);
      }
    }
  }, [location.search, isLoading, plrList]);

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', label: 'Pendente', icon: AlertCircle },
      PENDING_APPROVAL: { color: 'bg-orange-100 text-orange-800', label: 'Aprovação Pendente', icon: AlertCircle },
      APPROVED: { color: 'bg-blue-100 text-blue-800', label: 'Aprovado', icon: CheckCircle },
      PAID: { color: 'bg-green-100 text-green-800', label: 'Pago', icon: CheckCircle },
      CANCELLED: { color: 'bg-red-100 text-red-800', label: 'Cancelado', icon: XCircle },
    };
    const badge = badges[status] || badges.PENDING;
    const Icon = badge.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Participação de Lucros (PLR)</h1>
        <p className="text-gray-600">Gerencie a participação de lucros para funcionários gerenciais (meses 4, 8 e 12)</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todos</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todos</option>
              <option value="4">1º Trimestre (Abril)</option>
              <option value="8">2º Trimestre (Agosto)</option>
              <option value="12">3º Trimestre (Dezembro)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
            <input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              min="2020"
              max="2100"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => handleOpenModal()}
              className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 flex items-center justify-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Nova PLR
            </button>
            {selectedPeriod && selectedYear && (
              <button
                onClick={() => {
                  setConfirmPayslip({ show: true, period: parseInt(selectedPeriod), year: selectedYear });
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center justify-center gap-2"
                title="Gerar Holerites PLR"
              >
                <FileText className="h-5 w-5" />
                Gerar Holerites
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : plrList.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Nenhuma PLR encontrada</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Funcionário</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Período</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ano</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aprovado em</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plrList.map((plr) => (
                  <tr key={plr.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{plr.user_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{getPeriodLabel(plr.period)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{plr.ref_year}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(plr.amount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(plr.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {plr.approved_at ? formatDate(plr.approved_at) : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {(plr.status === 'PENDING' || plr.status === 'PENDING_APPROVAL') && 
                         currentUser?.role === 'GERENCIA' && 
                         !plr.approvals_list?.some(a => String(a.id) === String(currentUser.id)) && (
                          <button
                            onClick={() => {
                              setConfirmApprove({ show: true, plrId: plr.id });
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="Aprovar"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                        {plr.status === 'APPROVED' && (
                          <button
                            onClick={() => {
                              setConfirmMarkPaid({ show: true, plrId: plr.id });
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="Marcar como paga"
                          >
                            <DollarSign className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenModal(plr)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {(plr.status === 'PENDING' || plr.status === 'PENDING_APPROVAL') && (
                          <button
                            onClick={() => {
                              setConfirmDelete({ show: true, plrId: plr.id });
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Remover"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingPLR ? 'Editar PLR' : 'Nova PLR'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário *</label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Selecione...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Período *</label>
                <select
                  value={formData.period}
                  onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Selecione...</option>
                  <option value="4">1º Trimestre (Abril)</option>
                  <option value="8">2º Trimestre (Agosto)</option>
                  <option value="12">3º Trimestre (Dezembro)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ano *</label>
                <input
                  type="number"
                  min="2020"
                  max="2100"
                  value={formData.ref_year}
                  onChange={(e) => setFormData({ ...formData, ref_year: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmPayslip.show}
        onConfirm={() => {
          generatePayslipsMutation.mutate({ period: confirmPayslip.period, year: confirmPayslip.year });
          setConfirmPayslip({ show: false, period: null, year: null });
        }}
        onCancel={() => setConfirmPayslip({ show: false, period: null, year: null })}
        title="Gerar Holerites PLR"
        message={`Gerar holerites PLR para ${getPeriodLabel(confirmPayslip.period)} de ${confirmPayslip.year}?`}
        type="success"
        confirmText="Gerar"
      />

      <ConfirmModal
        show={confirmApprove.show}
        onConfirm={() => {
          approveMutation.mutate(confirmApprove.plrId);
          setConfirmApprove({ show: false, plrId: null });
        }}
        onCancel={() => setConfirmApprove({ show: false, plrId: null })}
        title="Aprovar PLR"
        message="Confirma a aprovação desta PLR?"
        type="success"
        confirmText="Aprovar"
      />

      <ConfirmModal
        show={confirmMarkPaid.show}
        onConfirm={() => {
          markPaidMutation.mutate(confirmMarkPaid.plrId);
          setConfirmMarkPaid({ show: false, plrId: null });
        }}
        onCancel={() => setConfirmMarkPaid({ show: false, plrId: null })}
        title="Marcar como Paga"
        message="Tem certeza que deseja marcar esta PLR como paga?"
        type="warning"
        confirmText="Marcar"
      />

      <ConfirmModal
        show={confirmDelete.show}
        onConfirm={() => {
          deleteMutation.mutate(confirmDelete.plrId);
          setConfirmDelete({ show: false, plrId: null });
        }}
        onCancel={() => setConfirmDelete({ show: false, plrId: null })}
        title="Remover PLR"
        message="Tem certeza que deseja remover esta PLR?"
        type="danger"
        confirmText="Remover"
      />
    </div>
  );
};

export default ProfitSharingManagement;
